import { apiClient } from './client';
import type {
  EventTemplateWithComponents,
  TemplateInstance,
  ApplyTemplateDto,
  ApplyTemplateResult,
  CreateEventTemplateDto,
  UpdateEventTemplateDto,
} from '@/types/eventTemplate.types';

class EventTemplateService {
  /**
   * List all event templates, optionally filtered by search keyword
   */
  async getAll(search?: string): Promise<EventTemplateWithComponents[]> {
    const params = new URLSearchParams();
    if (search?.trim()) params.set('search', search.trim());
    const qs = params.toString();
    const response = await apiClient.get(`/event-templates${qs ? `?${qs}` : ''}`);
    return response.data.data || [];
  }

  /**
   * Get an event template by ID with all its components
   */
  async getById(id: number): Promise<EventTemplateWithComponents> {
    const response = await apiClient.get(`/event-templates/${id}`);
    return response.data.data;
  }

  /**
   * Create a new event template
   */
  async create(dto: CreateEventTemplateDto): Promise<EventTemplateWithComponents> {
    const response = await apiClient.post('/event-templates', dto);
    return response.data.data;
  }

  /**
   * Update an event template
   */
  async update(id: number, dto: UpdateEventTemplateDto): Promise<EventTemplateWithComponents> {
    const response = await apiClient.put(`/event-templates/${id}`, dto);
    return response.data.data;
  }

  /**
   * Delete an event template (soft delete)
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/event-templates/${id}`);
  }

  /**
   * Apply an event template to an entity (event or celebration)
   * Creates activities, attaches goals, etc.
   */
  async apply(
    templateId: number,
    dto: ApplyTemplateDto
  ): Promise<ApplyTemplateResult> {
    const response = await apiClient.post(`/event-templates/${templateId}/apply`, dto);
    return response.data.data;
  }

  /**
   * Get applied template instances for an entity
   */
  async getInstances(
    entityType: string,
    entityId: number
  ): Promise<TemplateInstance[]> {
    const response = await apiClient.get(`/event-templates/instances/${entityType}/${entityId}`);
    return response.data.data || [];
  }

  /**
   * Get suggested event templates for an entity based on its celebration template
   */
  async getSuggestions(
    entityId: number,
    entityType: 'event' | 'celebration'
  ): Promise<EventTemplateWithComponents[]> {
    const response = await apiClient.get(`/event-templates/suggestions/${entityType}/${entityId}`);
    return response.data.data || [];
  }
}

export const eventTemplateService = new EventTemplateService();
